<template>
  <div class="responsabilidade-social-page">
    <HomeHeader />
    
    <!-- Hero Section com Efeito Parallax -->
    <section class="hero-section">
      <div class="parallax-container">
        <div class="overlay"></div>
        <h1 class="hero-title">{{ t('responsabilidadeSocial.heroTitle') }}</h1>
        <div class="hero-subtitle">{{ t('responsabilidadeSocial.heroSubtitle') }}</div>
      </div>
    </section>
    
    <!-- Seção de Introdução -->
    <section class="intro-section">
      <div class="stats-watermark stats-watermark-1"></div>
      <div class="stats-watermark stats-watermark-2"></div>
      <div class="container">
        <ScrollReveal direction="bottom" :delay="100">
          <div class="section-header">
            <h2>{{ t('responsabilidadeSocial.overviewTitle') }}</h2>
            <div class="header-line"></div>
          </div>
          <p class="intro-text">{{ t('responsabilidadeSocial.overviewText') }}</p>
        </ScrollReveal>
        
        <div class="values-grid">
          <ScrollReveal direction="left" :delay="200">
            <div class="value-item">
              <div class="value-dot"></div>
              <div class="value-content">
                <h4>{{ t('responsabilidadeSocial.values.sustainability.title') }}</h4>
                <p>{{ t('responsabilidadeSocial.values.sustainability.description') }}</p>
              </div>
            </div>
          </ScrollReveal>
          
          <ScrollReveal direction="right" :delay="300">
            <div class="value-item">
              <div class="value-dot"></div>
              <div class="value-content">
                <h4>{{ t('responsabilidadeSocial.values.community.title') }}</h4>
                <p>{{ t('responsabilidadeSocial.values.community.description') }}</p>
              </div>
            </div>
          </ScrollReveal>
          
          <ScrollReveal direction="left" :delay="400">
            <div class="value-item">
              <div class="value-dot"></div>
              <div class="value-content">
                <h4>{{ t('responsabilidadeSocial.values.innovation.title') }}</h4>
                <p>{{ t('responsabilidadeSocial.values.innovation.description') }}</p>
              </div>
            </div>
          </ScrollReveal>
          
          <ScrollReveal direction="right" :delay="500">
            <div class="value-item">
              <div class="value-dot"></div>
              <div class="value-content">
                <h4>{{ t('responsabilidadeSocial.values.ethics.title') }}</h4>
                <p>{{ t('responsabilidadeSocial.values.ethics.description') }}</p>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </div>
    </section>
    
    <!-- Seção de Iniciativas com Cards Horizontais -->
    <section class="initiatives-section">
      <div class="watermark-side watermark-side-right"></div>
      <div class="container">
        <ScrollReveal direction="bottom" :delay="100">
          <div class="section-header">
            <h2>{{ t('responsabilidadeSocial.initiativesTitle') }}</h2>
            <div class="header-line"></div>
          </div>
        </ScrollReveal>
        
        <div class="initiatives-list">
          <ScrollReveal direction="left" :delay="200">
            <div class="initiative-card">
              <div class="initiative-number">01</div>
              <div class="initiative-icon">
                <i class="fas fa-recycle"></i>
              </div>
              <div class="initiative-content">
                <h3>{{ t('responsabilidadeSocial.initiatives.wasteManagement.title') }}</h3>
                <p>{{ t('responsabilidadeSocial.initiatives.wasteManagement.description') }}</p>
              </div>
            </div>
          </ScrollReveal>
          
          <ScrollReveal direction="right" :delay="300">
            <div class="initiative-card">
              <div class="initiative-number">02</div>
              <div class="initiative-icon">
                <i class="fas fa-solar-panel"></i>
              </div>
              <div class="initiative-content">
                <h3>{{ t('responsabilidadeSocial.initiatives.energyEfficiency.title') }}</h3>
                <p>{{ t('responsabilidadeSocial.initiatives.energyEfficiency.description') }}</p>
              </div>
            </div>
          </ScrollReveal>
          
          <ScrollReveal direction="left" :delay="400">
            <div class="initiative-card">
              <div class="initiative-number">03</div>
              <div class="initiative-icon">
                <i class="fas fa-heart"></i>
              </div>
              <div class="initiative-content">
                <h3>{{ t('responsabilidadeSocial.initiatives.socialProjects.title') }}</h3>
                <p>{{ t('responsabilidadeSocial.initiatives.socialProjects.description') }}</p>
              </div>
            </div>
          </ScrollReveal>
          
          <ScrollReveal direction="right" :delay="500">
            <div class="initiative-card">
              <div class="initiative-number">04</div>
              <div class="initiative-icon">
                <i class="fas fa-stethoscope"></i>
              </div>
              <div class="initiative-content">
                <h3>{{ t('responsabilidadeSocial.initiatives.healthCampaigns.title') }}</h3>
                <p>{{ t('responsabilidadeSocial.initiatives.healthCampaigns.description') }}</p>
              </div>
            </div>
          </ScrollReveal>
          
          <ScrollReveal direction="left" :delay="600">
            <div class="initiative-card">
              <div class="initiative-number">05</div>
              <div class="initiative-icon">
                <i class="fas fa-hands-helping"></i>
              </div>
              <div class="initiative-content">
                <h3>{{ t('responsabilidadeSocial.initiatives.diversity.title') }}</h3>
                <p>{{ t('responsabilidadeSocial.initiatives.diversity.description') }}</p>
              </div>
            </div>
          </ScrollReveal>
          
          <ScrollReveal direction="right" :delay="700">
            <div class="initiative-card">
              <div class="initiative-number">06</div>
              <div class="initiative-icon">
                <i class="fas fa-handshake"></i>
              </div>
              <div class="initiative-content">
                <h3>{{ t('responsabilidadeSocial.initiatives.partnerships.title') }}</h3>
                <p>{{ t('responsabilidadeSocial.initiatives.partnerships.description') }}</p>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </div>
    </section>
    
    <!-- Seção de CTA Simples -->
    <section class="cta-section">
      <div class="stats-watermark-center"></div>
      <div class="container">
        <ScrollReveal direction="bottom" :delay="100">
          <div class="cta-content">
            <h2>{{ t('responsabilidadeSocial.contactTitle') }}</h2>
            <p>{{ t('responsabilidadeSocial.contactText') }}</p>
            <router-link to="/Contato" class="cta-button">
              {{ t('responsabilidadeSocial.contactButton') }}
              <i class="fas fa-arrow-right"></i>
            </router-link>
          </div>
        </ScrollReveal>
      </div>
    </section>
    
    <HomeFooter />
  </div>
</template>

<script>
import HomeHeader from '@/components/HomeHeader.vue';
import HomeFooter from '@/components/HomeFooter.vue';
import { useSocialResponsibilityTranslation } from '@/composables/useSocialResponsibilityTranslation.js';

export default {
  components: {
    HomeHeader,
    HomeFooter
  },
  setup() {
    const { t, currentLanguage } = useSocialResponsibilityTranslation()
    
    return {
      t,
      currentLanguage
    }
  }
}
</script>

<style scoped>
/* Estilos Gerais */
.responsabilidade-social-page {
  font-family: 'Matter', 'Segoe UI', Arial, sans-serif;
  color: #333;
  line-height: 1.6;
  overflow-x: hidden;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

section {
  padding: 80px 0;
}

/* Hero Section com Parallax */
.hero-section {
  position: relative;
  height: 70vh;
  min-height: 500px;
  overflow: hidden;
  border-bottom: 4px solid #931414;
}

.parallax-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url('@/assets/header-responsabilidade-social.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  background-attachment: fixed;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  color: white;
}

.overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(rgba(147, 20, 20, 0.7), rgba(0, 0, 0, 0.7));
}

.hero-title {
  font-size: 3.5rem;
  font-weight: 800;
  margin-bottom: 20px;
  position: relative;
  z-index: 2;
  text-transform: uppercase;
  letter-spacing: 2px;
  animation: fadeInUp 1.5s ease-out;
}

.hero-subtitle {
  font-size: 1.3rem;
  font-weight: 300;
  position: relative;
  z-index: 2;
  animation: fadeInUp 1.5s ease-out 0.3s both;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Seção de Introdução */
.intro-section {
  padding: 100px 0;
  background: #f0f0f0;
  position: relative;
  overflow: hidden;
}

/* Símbolo da marca duplicado ao fundo, desmaiado, nos dois cantos opostos */
.stats-watermark {
  position: absolute;
  top: 0;
  width: 700px;
  height: 700px;
  background-image: url('@/assets/uni-simbolo-outline.svg');
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  opacity: 0.16;
  pointer-events: none;
  z-index: 0;
}

.stats-watermark-1 {
  left: -181px;
  top: -203px;
  mask-image: linear-gradient(to bottom right, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0.6) 55%, transparent 85%);
  -webkit-mask-image: linear-gradient(to bottom right, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0.6) 55%, transparent 85%);
}

.stats-watermark-2 {
  right: -203px;
  left: auto;
  top: auto;
  bottom: -226px;
  mask-image: linear-gradient(to top left, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0.6) 55%, transparent 85%);
  -webkit-mask-image: linear-gradient(to top left, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0.6) 55%, transparent 85%);
}

@media (min-width: 769px) and (max-height: 820px) {
  .stats-watermark-2 {
    display: none;
  }
}

@media (max-width: 768px) {
  .stats-watermark-2 {
    display: none;
  }
}

.section-header {
  text-align: center;
  margin-bottom: 60px;
}

.section-header h2 {
  font-size: 2.5rem;
  font-weight: 700;
  color: #931414;
  margin-bottom: 16px;
}

.header-line {
  width: 80px;
  height: 4px;
  background: linear-gradient(90deg, #931414, #470404);
  margin: 0 auto;
  border-radius: 2px;
}

.intro-text {
  font-size: 1.2rem;
  color: #555;
  line-height: 1.8;
  text-align: center;
  max-width: 800px;
  margin: 0 auto 60px;
}

.values-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 40px;
  max-width: 1000px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
}

.value-item {
  display: flex;
  gap: 20px;
  align-items: flex-start;
  background: white;
  padding: 30px;
  border-radius: 15px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  border-left: 4px solid #931414;
}

.value-item:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 30px rgba(147, 20, 20, 0.15);
}

.value-dot {
  width: 16px;
  height: 16px;
  background: #931414;
  border-radius: 50%;
  margin-top: 6px;
  flex-shrink: 0;
}

.value-content h4 {
  font-size: 1.3rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
}

.value-content p {
  font-size: 1rem;
  color: #696969;
  line-height: 1.6;
}

/* Seção de Iniciativas */
.initiatives-section {
  padding: 100px 0;
  background: linear-gradient(to bottom, #ffffff, #f0f0f0);
  position: relative;
  overflow: hidden;
}

/* Símbolo da marca único, sangrando pela lateral direita */
.watermark-side {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 1008px;
  height: 1008px;
  background-image: url('@/assets/uni-simbolo-outline.svg');
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  opacity: 0.15;
  pointer-events: none;
  z-index: 0;
}

.watermark-side-right {
  right: -399px;
  mask-image: linear-gradient(to left, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0.5) 55%, transparent 85%);
  -webkit-mask-image: linear-gradient(to left, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0.5) 55%, transparent 85%);
}

.initiatives-list {
  display: flex;
  flex-direction: column;
  gap: 30px;
  max-width: 900px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
}

.initiative-card {
  display: flex;
  align-items: center;
  gap: 30px;
  background: #f0f0f0;
  padding: 35px;
  border-radius: 20px;
  transition: all 0.3s ease;
  border: 2px solid transparent;
  position: relative;
  overflow: hidden;
}

.initiative-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, rgba(147, 20, 20, 0.02), rgba(71, 4, 4, 0.02));
  z-index: 0;
}

.initiative-card:hover {
  transform: translateX(10px);
  border-color: #931414;
  box-shadow: 0 10px 30px rgba(147, 20, 20, 0.1);
}

.initiative-number {
  font-size: 2.5rem;
  font-weight: 700;
  color: rgba(147, 20, 20, 0.3);
  min-width: 60px;
  position: relative;
  z-index: 1;
}

.initiative-icon {
  width: 70px;
  height: 70px;
  background: linear-gradient(135deg, #931414, #470404);
  border-radius: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  position: relative;
  z-index: 1;
}

.initiative-icon i {
  font-size: 1.8rem;
  color: white;
}

.initiative-content {
  flex: 1;
  position: relative;
  z-index: 1;
}

.initiative-content h3 {
  font-size: 1.4rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 10px;
}

.initiative-content p {
  font-size: 1rem;
  color: #696969;
  line-height: 1.6;
}

/* Seção CTA */
.cta-section {
  padding: 80px 0;
  background: linear-gradient(135deg, #931414, #470404);
  color: white;
  text-align: center;
  position: relative;
  overflow: hidden;
}

/* Símbolo da marca único, centralizado e expandido (fundo sólido vermelho) */
.stats-watermark-center {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url('@/assets/uni-simbolo-outline-branco.svg');
  background-repeat: no-repeat;
  background-position: center;
  background-size: 1100px;
  opacity: 0.14;
  pointer-events: none;
  z-index: 0;
}

.cta-section .container {
  position: relative;
  z-index: 1;
}

.cta-content h2 {
  font-size: 2.2rem;
  font-weight: 700;
  margin-bottom: 16px;
}

.cta-content p {
  font-size: 1.1rem;
  margin-bottom: 32px;
  opacity: 0.9;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
}

/* .cta-button está na regra global de glass, que forçaria um
   preenchimento vermelho translúcido aqui — mas o fundo desta seção já
   é bordô, então o botão precisa continuar sólido branco (senão fica
   vermelho sobre vermelho, ilegível) */
.cta-button {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.8), rgba(240, 240, 240, 0.65)) !important;
  backdrop-filter: blur(20px) saturate(160%) !important;
  -webkit-backdrop-filter: blur(20px) saturate(160%) !important;
  border: 1px solid rgba(255, 255, 255, 0.6) !important;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.7) !important;
  color: #931414 !important;
  -webkit-text-fill-color: #931414 !important;
  padding: 16px 32px;
  border-radius: 50px;
  text-decoration: none;
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.3s ease;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.cta-button i {
  background-image: none !important;
  -webkit-background-clip: initial !important;
  background-clip: initial !important;
  -webkit-text-fill-color: #931414 !important;
  color: #931414 !important;
}

.cta-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
}

.cta-button i {
  transition: transform 0.3s ease;
}

.cta-button:hover i {
  transform: translateX(4px);
}

/* Responsividade */
@media (max-width: 992px) {
  .hero-title {
    font-size: 3rem;
  }
  
  .hero-subtitle {
    font-size: 1.2rem;
  }
  
  section {
    padding: 60px 0;
  }
  
  .values-grid {
    grid-template-columns: 1fr;
    gap: 30px;
  }
  
  .initiative-card {
    flex-direction: column;
    text-align: center;
    gap: 25px;
  }
  
  .initiative-number {
    order: -1;
    font-size: 3.5rem;
    color: #931414;
    opacity: 0.2;
  }
  
  .initiative-card:hover {
    transform: translateY(-5px);
  }
}

@media (max-width: 768px) {
  .hero-section {
    height: 50vh;
    min-height: 350px;
  }
  
  .hero-title {
    font-size: 2.5rem;
  }

  .parallax-container {
    background-attachment: scroll;
  }
  
  .section-header h2 {
    font-size: 2rem;
  }
  
  .intro-text {
    font-size: 1.1rem;
  }
  
  .values-grid {
    grid-template-columns: 1fr;
    gap: 25px;
  }
  
  .value-item {
    padding: 25px;
  }
  
  .initiative-card {
    padding: 25px;
  }
  
  .initiative-icon {
    width: 60px;
    height: 60px;
  }
  
  .initiative-icon i {
    font-size: 1.5rem;
  }
}

@media (max-width: 576px) {
  .container {
    padding: 0 16px;
  }
  
  .hero-title {
    font-size: 2rem;
  }
  
  .hero-subtitle {
    font-size: 1rem;
  }
  
  .section-header h2 {
    font-size: 1.8rem;
  }
  
  .header-line {
    width: 60px;
  }
  
  .intro-section,
  .initiatives-section {
    padding: 60px 0;
  }
  
  .value-item {
    padding: 20px;
    gap: 15px;
  }
  
  .value-content h4 {
    font-size: 1.1rem;
  }
  
  .value-content p {
    font-size: 0.9rem;
  }
  
  .initiative-card {
    padding: 20px;
  }
  
  .initiative-content h3 {
    font-size: 1.2rem;
  }
  
  .initiative-content p {
    font-size: 0.9rem;
  }
  
  .initiative-icon {
    width: 50px;
    height: 50px;
  }
  
  .initiative-icon i {
    font-size: 1.2rem;
  }
  
  .cta-section {
    padding: 60px 0;
  }
  
  .cta-content h2 {
    font-size: 1.8rem;
  }
  
  .cta-button {
    padding: 14px 28px;
    font-size: 0.9rem;
  }
}

@media (max-width: 400px) {
  .hero-title {
    font-size: 1.8rem;
  }
  
  .section-header h2 {
    font-size: 1.6rem;
  }
  
  .value-item {
    flex-direction: column;
    text-align: center;
    gap: 10px;
  }
  
  .value-dot {
    margin: 0 auto;
  }
  
  .initiative-number {
    font-size: 3rem;
  }
  
  .cta-content h2 {
    font-size: 1.6rem;
  }
}

/* Melhorias de acessibilidade */
.initiative-card:focus-within {
  outline: 2px solid #931414;
  outline-offset: 2px;
}

.cta-button:focus {
  outline: 2px solid white;
  outline-offset: 2px;
}

.value-item:focus-within {
  outline: 2px solid #931414;
  outline-offset: 2px;
}

/* Transições suaves */
* {
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
}

/* Scroll suave */
html {
  scroll-behavior: smooth;
}

/* Estados de hover melhorados */
.value-item:hover .value-dot {
  transform: scale(1.3);
  transition: transform 0.2s ease;
}

.initiative-card:hover .initiative-icon {
  transform: scale(1.05);
}

.initiative-card:hover .initiative-number {
  color: #931414;
  opacity: 0.6;
}

/* Efeitos de entrada */
.value-item {
  animation: slideInUp 0.6s ease-out;
}

.initiative-card {
  animation: slideInLeft 0.6s ease-out;
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideInLeft {
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

/* Otimizações de performance */
.initiative-card,
.value-item,
.cta-button {
  will-change: transform;
}

/* Tipografia melhorada */
.hero-title,
.section-header h2,
.cta-content h2 {
  letter-spacing: -0.025em;
}

/* Estados ativos */
.cta-button:active {
  transform: translateY(0);
}

.initiative-card:active {
  transform: translateX(5px);
}

.value-item:active {
  transform: translateY(-2px);
}

/* Efeito de brilho sutil */
.initiative-icon::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.3), transparent);
  transform: rotate(45deg);
  transition: all 0.6s;
  opacity: 0;
}

.initiative-card:hover .initiative-icon::before {
  opacity: 1;
  transform: rotate(45deg) translate(50%, 50%);
}

/* Padrão de fundo sutil */
.intro-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: repeating-linear-gradient(45deg, rgba(240, 240, 240, 0.05) 0px, rgba(240, 240, 240, 0.05) 1px, transparent 1px, transparent 14px);
  z-index: 0;
}

.intro-section {
  position: relative;
  overflow: hidden;
}

/* Símbolo em dourado sangrando pelo canto superior direito */
.intro-section::after {
  content: '';
  position: absolute;
  top: -357px;
  right: -336px;
  width: 1050px;
  height: 1050px;
  background-image: url('@/assets/uni-simbolo-outline-dourado.svg');
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  mask-image: linear-gradient(225deg, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0.5) 55%, transparent 85%);
  -webkit-mask-image: linear-gradient(225deg, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0.5) 55%, transparent 85%);
  pointer-events: none;
  opacity: 0.16;
  z-index: 0;
}

.intro-section .container {
  position: relative;
  z-index: 1;
}

/* Gradientes sutis nos cards */
.value-item {
  background: linear-gradient(145deg, #ffffff, #f0f0f0);
  border: 1px solid rgba(147, 20, 20, 0.1);
}

.value-item:hover {
  border-color: rgba(147, 20, 20, 0.2);
}

/* Sombras personalizadas */
.initiative-card {
  box-shadow: 0 4px 15px rgba(147, 20, 20, 0.08);
}

.initiative-card:hover {
  box-shadow: 0 8px 25px rgba(147, 20, 20, 0.15);
}

/* Efeito de pulsação para os ícones */
@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(147, 20, 20, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(147, 20, 20, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(147, 20, 20, 0);
  }
}

.initiative-icon:hover {
  animation: pulse 1.5s infinite;
}
</style>
